import React, { useContext, useEffect, useRef, useState } from 'react'
import { Button } from '@mui/material'
import FormattedPrice from '@components/FormattedPrice'
import { Trans, useTranslation } from 'react-i18next'
import { OutOfStockStatusEnum } from '../../../../generated/gqlTypes'
import ProductFormFixedButtons from './ProductFormFixedButtons'
import { Element } from 'react-scroll'
import ProductContext from '../context/ProductContext'
import useWholesaleCartButton from './useWholesaleCartButton'

const SCROLL_BUTTONS_ID = 'product-form-buttons'

interface Props {
  checkVariantAvailability(): boolean

  selectedVariant: {
    shippingRequired: boolean
    isInCart: boolean
    isSupply: boolean
    isCustomizable: boolean
    hasDefaultKit: boolean
    stockQuantity: number
    stockStatus: OutOfStockStatusEnum | null
  }

  onAddToBag(): Promise<void>

  onAddToBagFullKit(): Promise<void>

  onCustomizeKit(): Promise<void>

  isArchive: boolean

  onDownloadFiles(): void

  isInPack: boolean
  zipFileLink: string
}

const ProductFormButtons: React.FC<Props> = ({
  checkVariantAvailability,
  selectedVariant,
  onAddToBag,
  onAddToBagFullKit,
  onCustomizeKit,
  isArchive,
  onDownloadFiles,
  isInPack,
  zipFileLink,
}) => {
  const { t } = useTranslation()
  const { formValues, priceRange, defaultKitPrice, isWholesale, cartQuantity, removing, onRemoveFromCart } =
    useContext(ProductContext)
  const wholesaleButton = useWholesaleCartButton()
  // For kits this is the number of kits, so the button shows the price for all of them.
  const quantity = formValues.quantity || 1
  const ref = useRef(null)
  const [isVisible, setIsVisible] = useState(false)
  const [width, setWidth] = useState(0)

  useEffect(() => {
    if (!ref.current) return

    const observer = new ResizeObserver(([entry]) => {
      setWidth(entry.contentRect.width)
    })

    observer.observe(ref.current)

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.1 } // 10% visible
    )

    if (ref.current) observer.observe(ref.current)
    return () => observer.disconnect()
  }, [])

  const renderButtons = () => {
    if (!selectedVariant) {
      return (
        <div>
          <Button variant='contained' color='secondary' size='large' onClick={onAddToBag} fullWidth disabled>
            {t('button.addToBag', 'Add to cart')}
          </Button>
        </div>
      )
    }
    if (!isWholesale && isInPack && !selectedVariant?.shippingRequired) {
      return (
        <div>
          {zipFileLink && (
            <Button
              variant='contained'
              color='secondary'
              size='large'
              onClick={onDownloadFiles}
              // href={zipFileLink}
              // download
              fullWidth
            >
              {t('button.download', 'Download')}
            </Button>
          )}
          {!zipFileLink && (
            <Button variant='contained' color='secondary' size='large' onClick={onDownloadFiles} fullWidth>
              {t('button.contactUsForMoreAttempts', 'Contact us for more attempts')}
            </Button>
          )}
        </div>
      )
    }
    // A digital pattern is bought once in retail; wholesale buys copies, whose quantity the form sets.
    const inBagDigital = !isWholesale && selectedVariant.isInCart && !selectedVariant.shippingRequired
    const isSupply = selectedVariant.isSupply
    const isAvailable = checkVariantAvailability()
    // Wholesale: a kit in the cart gets its quantity updated, even a customizable one without a saved default.
    const showDefaultKitButton = selectedVariant.hasDefaultKit || !!wholesaleButton.label
    return (
      <div>
        <div>
          {!isSupply && (
            <div>
              <div>
                <Button
                  variant='contained'
                  color='secondary'
                  size='large'
                  onClick={onAddToBag}
                  disabled={!isAvailable || inBagDigital || isArchive || wholesaleButton.isAdded}
                  disableElevation
                  fullWidth
                >
                  {isArchive && t('button.archive', 'Archive')}
                  {!isArchive
                    ? inBagDigital
                      ? t('button.alreadyInTheBag', 'Already in the cart')
                      : wholesaleButton.label || t('button.addToBag', 'Add to cart')
                    : ''}
                </Button>
              </div>
            </div>
          )}
          {isSupply && (
            <>
              {!selectedVariant.isCustomizable && (
                <Button
                  variant='contained'
                  color='secondary'
                  size='large'
                  onClick={onAddToBagFullKit}
                  disabled={!isAvailable || isArchive || wholesaleButton.isAdded}
                  disableElevation
                  fullWidth
                >
                  {wholesaleButton.label || (
                    <Trans
                      i18nKey='button.addToBagFullKitPrice'
                      defaults='Add to cart <price/>'
                      components={{
                        price: (
                          <FormattedPrice currency={priceRange.currency} amount={priceRange.discount.max * quantity} />
                        ),
                      }}
                    />
                  )}
                </Button>
              )}
              {selectedVariant.isCustomizable && (
                <>
                  {showDefaultKitButton && (
                    <>
                      <Button
                        variant='contained'
                        color='secondary'
                        size='large'
                        onClick={onAddToBagFullKit}
                        disabled={!isAvailable || isArchive || wholesaleButton.isAdded}
                        disableElevation
                        fullWidth
                      >
                        {wholesaleButton.label ? (
                          wholesaleButton.label
                        ) : defaultKitPrice ? (
                          <Trans
                            i18nKey='button.addDefaultKitToCartPrice'
                            defaults='Add Default KIT to Cart — <price/>'
                            components={{
                              price: (
                                <FormattedPrice
                                  currency={defaultKitPrice.currency}
                                  amount={defaultKitPrice.gross * quantity}
                                />
                              ),
                            }}
                          />
                        ) : (
                          t('button.addDefaultKitToCart', 'Add Default KIT to Cart')
                        )}
                      </Button>
                      <div className='my-5' />
                    </>
                  )}
                  <Button
                    variant={showDefaultKitButton ? 'outlined' : 'contained'}
                    color='secondary'
                    size='large'
                    onClick={onCustomizeKit}
                    disabled={!isAvailable || isArchive}
                    disableElevation={!showDefaultKitButton}
                    fullWidth
                  >
                    {t('button.customizeTheKit', 'Customize the KIT')}
                  </Button>
                </>
              )}
            </>
          )}
          {isWholesale && cartQuantity > 0 && (
            <>
              <div className='my-5' />
              <Button
                variant='outlined'
                color='secondary'
                size='large'
                onClick={onRemoveFromCart}
                disabled={removing}
                fullWidth
              >
                {t('button.remove', 'Remove')}
              </Button>
            </>
          )}
        </div>
      </div>
    )
  }

  return (
    <Element name={SCROLL_BUTTONS_ID}>
      <div ref={ref}>
        {renderButtons()}
        {!isVisible && <ProductFormFixedButtons width={width} scrollId={SCROLL_BUTTONS_ID} />}
      </div>
    </Element>
  )
}

export default ProductFormButtons
